// Voice controls shown during an interactive session: mic mute, speaker mute, the ⋮ "more" menu
// (captions + device pickers), and an optional End pill. Mic/speaker also double as live status —
// the mic pill highlights while listening, the speaker pill while speaking.
import { MicIcon, SpeakerIcon, WaveformIcon } from '@/components/chat/ChatIcons';
import { AudioMoreMenu, type AudioMoreMenuProps } from './AudioMoreMenu';

const pill = (active: boolean, muted = false) =>
  `flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition ${
    muted
      ? 'bg-danger/15 border-danger/30 text-danger-text hover:bg-danger/25'
      : active
        ? 'bg-ui-subtle border-ui-border text-ui-text'
        : 'bg-transparent border-ui-border text-ui-text2 hover:text-ui-text hover:border-ui-border3'
  }`;

export const VoicePills = ({
  recording,
  speaking,
  micMuted = false,
  onToggleMic,
  speakerMuted = false,
  onToggleSpeaker,
  moreMenu,
  onEnd,
}: {
  recording: boolean;
  /** SAM's voice is actually sounding right now (usePCMAudioStream's `playing`, already ANDed with
   *  !speakerMuted by the caller) — highlights the Speaker pill. */
  speaking: boolean;
  micMuted?: boolean;
  /** Omit to hide the Mic pill (text-only realtime test mode). */
  onToggleMic?: () => void;
  speakerMuted?: boolean;
  /** Omit to hide the Speaker pill (text-only realtime has no audio out). */
  onToggleSpeaker?: () => void;
  /** Captions toggle + mic/speaker device picker, surfaced via the ⋮ button. */
  moreMenu?: AudioMoreMenuProps;
  /** When provided, renders an End pill that stops the voice session. */
  onEnd?: () => void;
}) => {
  const listening = recording && !micMuted;
  return (
    <div className="flex items-center gap-2 px-4 pb-2 flex-wrap">
      {onToggleMic && (
      <button onClick={onToggleMic} title={micMuted ? 'Unmute microphone' : 'Mute microphone'} className={pill(listening, micMuted)}>
        <MicIcon className="h-3.5 w-3.5" /> {micMuted ? 'Mic muted' : 'Mic'}
      </button>
      )}
      {onToggleSpeaker && (
      <button onClick={onToggleSpeaker} title={speakerMuted ? 'Unmute speaker' : 'Mute speaker'} className={pill(speaking, speakerMuted)}>
        <SpeakerIcon className="h-3.5 w-3.5" /> {speakerMuted ? 'Speaker muted' : 'Speaker'}
      </button>
      )}
      {moreMenu && <AudioMoreMenu {...moreMenu} />}
      {onEnd && (
        <button onClick={onEnd} className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-xl bg-accent text-on-accent text-xs font-medium hover:bg-accent/80 transition">
          <WaveformIcon className="h-3.5 w-3.5" /> End
        </button>
      )}
    </div>
  );
};
